import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Share2, Check } from 'lucide-react';
import { PRODUCTS, CATEGORIES } from '../../data/smartcasesProducts';
import { SmartcasesCart } from './SmartcasesCart';
import { getCart, addToCart, formatEur } from './cart';

const priceLabel = (p) => {
  if (p.variants) {
    const prices = p.variants.map((v) => v.price);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? formatEur(min) : `ab ${formatEur(min)}`;
  }
  return formatEur(p.price);
};

const ProductCard = ({ product }) => {
  const [added, setAdded] = useState(false);
  const quickAdd = () => {
    addToCart({ slug: product.slug, name: product.name, price: product.price });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };
  return (
    <div className="card-ink card-ink--hover overflow-hidden flex flex-col" data-testid={`product-card-${product.slug}`}>
      <Link to={`/smartcases/${product.slug}`} className="block bg-white aspect-square overflow-hidden border-b-2 border-ink">
        {product.images[0] && <img src={product.images[0]} alt={product.name} loading="lazy" className="w-full h-full object-contain p-4" />}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/smartcases/${product.slug}`} className="font-display font-bold leading-snug hover:text-purple transition-colors" data-testid={`product-name-${product.slug}`}>{product.name}</Link>
        <p className="text-xs text-ink/70 mt-1 line-clamp-2 flex-1">{product.description}</p>
        <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
          <span className="font-display font-bold" data-testid={`product-price-${product.slug}`}>{priceLabel(product)}</span>
          {product.variants || product.hasPayment ? (
            <Link to={`/smartcases/${product.slug}`} className="btn btn--purple btn--xs" data-testid={`product-configure-${product.slug}`}>Konfigurieren</Link>
          ) : (
            <button onClick={quickAdd} className={`btn btn--xs ${added ? 'btn--wa' : 'btn--purple'}`} data-testid={`product-add-${product.slug}`}>
              {added ? '✓ Hinzugefügt' : 'Auf den Zettel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default function SmartcasesPage() {
  const [cartCount, setCartCount] = useState(getCart().reduce((s, i) => s + i.qty, 0));
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  const [shared, setShared] = useState(false);

  const sharePage = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/smartcases/share`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'SmartCases – gastro-beratung.de', url });
      } else {
        await navigator.clipboard.writeText(url);
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch (e) { /* abgebrochen */ }
  };

  useEffect(() => {
    const sync = () => setCartCount(getCart().reduce((s, i) => s + i.qty, 0));
    window.addEventListener('smartcases-cart', sync);
    return () => window.removeEventListener('smartcases-cart', sync);
  }, []);

  useEffect(() => { document.title = 'SmartCases – Robuste Kassen-Hardware | gastro-beratung.de'; }, []);

  return (
    <div className="min-h-screen bg-off">
      {/* Hero */}
      <div className="sec--deep border-b-2 border-ink">
        <div className="wrap max-w-7xl py-12">
          <span className="badge-ink badge-ink--lilac mb-5">Ein Service von gastro-beratung.de</span>
          <h1 className="display-1">Smart<span className="serif-it text-lilac">Cases</span></h1>
          <p className="opacity-85 mt-4 max-w-2xl text-lg">
            Cases, Ständer, Ladestationen und Zubehör für deine Gastronomie – als digitaler Einkaufszettel.
            Produkte auswählen, Kontaktdaten angeben und ein unverbindliches Angebot erhalten.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {Object.entries(CATEGORIES).map(([key, label]) => (
              <a key={key} href={`#${key}`} className="badge-ink badge-ink--white hover:bg-lilac transition-colors" data-testid={`category-chip-${key}`}>{label}</a>
            ))}
            <button onClick={sharePage} className="btn btn--lilac btn--xs" data-testid="share-page-button">
              {shared ? (<><Check className="w-3.5 h-3.5" /> Link kopiert!</>) : (<><Share2 className="w-3.5 h-3.5" /> Seite teilen</>)}
            </button>
          </div>
        </div>
      </div>

      <div className="wrap max-w-7xl py-10 flex gap-8">
        {/* Produktliste */}
        <div className="flex-1 min-w-0">
          {Object.entries(CATEGORIES).map(([key, label]) => {
            const items = PRODUCTS.filter((p) => p.category === key);
            if (items.length === 0) return null;
            return (
              <section key={key} id={key} className="mb-12 scroll-mt-6">
                <h2 className="display-3 mb-5 flex items-center gap-3"><span className="star" aria-hidden="true">✳</span>{label}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {items.map((p) => <ProductCard key={p.slug} product={p} />)}
                </div>
              </section>
            );
          })}
          <p className="text-xs text-ink/60">Alle Preise in Euro: unverbindliche Netto-Richtpreise zzgl. MwSt. und Versand. Angebot und Beratung erfolgen individuell. Optische Abweichungen möglich.</p>
        </div>

        {/* Einkaufszettel Sidebar (Desktop) */}
        <aside className="hidden lg:block w-96 flex-shrink-0">
          <div className="sticky top-24">
            <SmartcasesCart />
          </div>
        </aside>
      </div>

      {/* Mobile Cart Button + Drawer */}
      <button onClick={() => setMobileCartOpen(true)} className="lg:hidden fixed bottom-5 right-5 z-40 btn btn--purple" data-testid="mobile-cart-button">
        <ShoppingBag className="w-5 h-5" /> Zettel {cartCount > 0 && <span className="bg-lilac text-ink border border-ink text-xs px-2 py-0.5 font-sans">{cartCount}</span>}
      </button>
      {mobileCartOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-ink/70" onClick={() => setMobileCartOpen(false)}>
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto border-t-2 border-ink bg-off p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end mb-2">
              <button onClick={() => setMobileCartOpen(false)} className="btn btn--xs btn--white" data-testid="mobile-cart-close">Schließen ✕</button>
            </div>
            <SmartcasesCart />
          </div>
        </div>
      )}
    </div>
  );
}
